/**
 *  @class
 *  @function ImageTextOverlay
 *
 *  Salesforce Data Cloud (360) personalisation
 *  -------------------------------------------
 *  When the merchant fills in the section's "Personalisation ID" setting,
 *  the element will carry a `data-personalisation-id` attribute. On
 *  connection we:
 *    1. Wait for `window.SalesforceInteractions.Personalization.fetch` to
 *       become available (up to 15s).
 *    2. Issue `Personalization.fetch([id])` once per Point name and cache
 *       the resolved promise (so section re-renders / re-mounts don't
 *       re-fetch).
 *    3. Apply each returned attribute to the corresponding DOM element,
 *       overriding only the attributes that were actually returned.
 *       Merchant defaults remain in place for everything else.
 *    4. Preload any personalised images BEFORE removing the skeleton so
 *       the reveal is flicker-free (matches the slideshow behaviour).
 *
 *  Supported attribute keys (with aliases / fallback):
 *    - Desktop_Image / Image / Image_URL / Desktop_Image_URL
 *    - Mobile_Image  / Mobile_Image_URL / MobileImage
 *    - Badge / Badge_Text
 *    - Subheading / Subheading_Text / Tagline
 *    - Heading / Heading_Text / Title
 *    - Description / Body / Body_Text / Text / Content
 *    - Button_Text / CTA_Text / ButtonText / Button_Label
 *    - Button_Link / CTA_Link / ButtonLink / Button_URL
 *
 *  Any attribute key not in the list above is ignored, but unknown
 *  attribute names are still logged so misnamed Content Zone attributes
 *  are easy to spot in the console.
 */

if (!customElements.get("image-with-text-overlay")) {
  const imageOverlayIsShiels = () => window.STORE_CONTEXT?.isShiels === true;

  const personalisationCache = new Map();
  const SDK_MAX_WAIT_MS = 15000;
  const SDK_POLL_INTERVAL_MS = 100;
  const SKELETON_TIMEOUT_MS = 6000;
  const IMAGE_PRELOAD_TIMEOUT_MS = 5000;

  const ATTRIBUTE_ALIASES = {
    desktopImage: ["Desktop_Image", "DesktopImage", "Image", "Image_URL", "Desktop_Image_URL", "desktop_image", "image", "image_url"],
    mobileImage: ["Mobile_Image", "MobileImage", "Mobile_Image_URL", "mobile_image", "mobile_image_url"],
    badge: ["Badge", "Badge_Text", "BadgeText", "badge", "badge_text"],
    subheading: ["Subheading", "Sub_Heading", "Subheading_Text", "SubheadingText", "Tagline", "subheading", "tagline"],
    heading: ["Heading", "Heading_Text", "HeadingText", "Title", "heading", "title"],
    description: ["Description", "Body", "Body_Text", "BodyText", "Text", "Content", "description", "body", "text", "content"],
    buttonText: ["Button_Text", "ButtonText", "CTA_Text", "CTAText", "Button_Label", "button_text", "cta_text"],
    buttonLink: ["Button_Link", "ButtonLink", "Button_URL", "ButtonURL", "CTA_Link", "CTALink", "Slide_Click_Link", "button_link", "cta_link"],
  };

  const pickAttribute = (attributes, aliases) => {
    if (!attributes) return null;
    for (const key of aliases) {
      const value = attributes[key];
      if (typeof value === "string" && value.trim().length > 0) {
        return { value, key };
      }
    }
    return null;
  };

  const sdkReady = () =>
    typeof window !== "undefined" &&
    window.SalesforceInteractions &&
    window.SalesforceInteractions.Personalization &&
    typeof window.SalesforceInteractions.Personalization.fetch === "function";

  const resolveSdk = () =>
    new Promise((resolve, reject) => {
      if (sdkReady()) return resolve(window.SalesforceInteractions);
      const started = Date.now();
      const interval = setInterval(() => {
        if (sdkReady()) {
          clearInterval(interval);
          resolve(window.SalesforceInteractions);
        } else if (Date.now() - started > SDK_MAX_WAIT_MS) {
          clearInterval(interval);
          reject(new Error("Salesforce Data Cloud SDK / Personalization.fetch not available"));
        }
      }, SDK_POLL_INTERVAL_MS);
    });

  const fetchPersonalisation = (personalisationId) => {
    if (personalisationCache.has(personalisationId)) {
      return personalisationCache.get(personalisationId);
    }
    const promise = resolveSdk()
      .then((SalesforceInteractions) => {
        console.log("[image-with-text-overlay] Fetching personalisation for", personalisationId);
        return SalesforceInteractions.Personalization.fetch([personalisationId]);
      })
      .then((response) => {
        const personalizations = (response && response.personalizations) || [];
        const match = personalizations.find(
          (p) => p && p.personalizationPointName === personalisationId
        );
        if (!match) {
          throw new Error(`No personalisation returned for "${personalisationId}"`);
        }
        return match;
      });
    personalisationCache.set(personalisationId, promise);
    promise.catch(() => personalisationCache.delete(personalisationId));
    return promise;
  };

  const imageOverlayEmitTrace = (eventType, context = {}) => {
    if (!imageOverlayIsShiels()) return;
    if (
      typeof window === "undefined" ||
      !window.SalesforceInteractions ||
      typeof window.SalesforceInteractions.sendEvent !== "function"
    ) {
      return;
    }
    const {
      personalisationId,
      contentId,
      decisionId,
      personalizationPointId,
      personalizationPointName,
      posture,
      attributes,
      source,
      href,
    } = context;
    try {
      window.SalesforceInteractions.sendEvent({
        interaction: {
          name: `imageWithTextOverlay_${eventType}`,
          eventType: "userEngagement",
          engagementType: eventType,
          source: source || "image_with_text_overlay",
          personalization: contentId ? { id: contentId, contentId: contentId } : undefined,
          personalizationPointName,
          personalizationPointId,
          contentId,
          decisionId,
          posture,
          href,
          attributes: attributes || {},
        },
      });
      console.log("[image-with-text-overlay] Trace", eventType, context);
    } catch (e) {
      console.warn("[image-with-text-overlay] Trace failed", eventType, e);
    }
  };

  const imageOverlayResolvePosture = (attributes) => {
    if (!attributes) return "active";
    const raw =
      attributes.Posture ||
      attributes.posture ||
      attributes.Variant ||
      attributes.Decision;
    if (!raw) return "active";
    const v = String(raw).toLowerCase().replace(/[\s\-]+/g, "_");
    if (v === "active" || v === "variant" || v === "show") return "active";
    if (v === "control" || v === "holdout") return "control";
    if (v === "suppressed" || v === "suppress") return "suppressed";
    if (v === "no_match" || v === "nomatch" || v === "hidden" || v === "hide") {
      return "no_match";
    }
    return "active";
  };

  const preloadImage = (url) =>
    new Promise((resolve) => {
      if (!url) return resolve(false);
      const img = new Image();
      let done = false;
      const finish = (ok) => {
        if (done) return;
        done = true;
        resolve(ok);
      };
      img.onload = () => finish(true);
      img.onerror = () => finish(false);
      setTimeout(() => finish(false), IMAGE_PRELOAD_TIMEOUT_MS);
      img.src = url;
    });

  class ImageTextOverlay extends HTMLElement {
    constructor() {
      super();

      this.tl = false;
      this.splittext = false;
    }
    connectedCallback() {
      if (document.body.classList.contains("animations-true") && typeof gsap !== "undefined") {
        // this.prepareAnimations();
      }

      this.initReadMore();

      const personalisationId = this.dataset.personalisationId;
      if (imageOverlayIsShiels() && personalisationId) {
        this.applyPersonalisation(personalisationId);
      }
    }

    initReadMore() {
      const readMoreBtns = this.querySelectorAll('.read-more-btn');

      readMoreBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();

          const mobileText = btn.closest('.mobile-text');
          const isExpanded = mobileText.classList.contains('expanded');

          if (isExpanded) {
            mobileText.classList.remove('expanded');
            btn.textContent = 'Read more';
          } else {
            mobileText.classList.add('expanded');
            btn.textContent = 'Read less';
          }
        });
      });
    }
    disconnectedCallback() {
      if (document.body.classList.contains("animations-true") && typeof gsap !== "undefined") {
        this.tl.kill();
        this.splittext.revert();
      }
    }

    applyPersonalisation(personalisationId) {
      if (!imageOverlayIsShiels()) return;

      const skeletonTimeout = setTimeout(() => this.clearSkeleton(), SKELETON_TIMEOUT_MS);

      fetchPersonalisation(personalisationId)
        .then((personalization) => {
          clearTimeout(skeletonTimeout);
          const attributes = (personalization && personalization.attributes) || {};
          const posture = imageOverlayResolvePosture(attributes);

          const contentId =
            personalization.personalizationId ||
            attributes.contentId ||
            attributes.ContentId ||
            null;
          if (contentId) this.dataset.personalisationContentId = contentId;
          if (personalization.decisionId) this.dataset.personalisationDecisionId = personalization.decisionId;
          if (personalization.personalizationPointId) this.dataset.personalisationPointId = personalization.personalizationPointId;
          if (personalization.personalizationPointName) this.dataset.personalisationPointName = personalization.personalizationPointName;
          this.dataset.personalisationPosture = posture;

          const traceContext = {
            personalisationId,
            contentId,
            decisionId: personalization.decisionId,
            personalizationPointId: personalization.personalizationPointId,
            personalizationPointName: personalization.personalizationPointName,
            posture,
            attributes,
          };
          this._imageOverlayTraceContext = traceContext;

          imageOverlayEmitTrace("decision", traceContext);

          if (posture === "control") {
            imageOverlayEmitTrace("control", traceContext);
            this.clearSkeleton();
            return;
          }
          if (posture === "suppressed") {
            imageOverlayEmitTrace("suppressed", traceContext);
            this.classList.add("image-with-text-overlay--suppressed");
            this.style.display = "none";
            this.clearSkeleton();
            return;
          }
          if (posture === "no_match") {
            imageOverlayEmitTrace("hidden_no_match", traceContext);
            this.clearSkeleton();
            return;
          }

          const picks = {
            desktopImage: pickAttribute(attributes, ATTRIBUTE_ALIASES.desktopImage),
            mobileImage: pickAttribute(attributes, ATTRIBUTE_ALIASES.mobileImage),
            badge: pickAttribute(attributes, ATTRIBUTE_ALIASES.badge),
            subheading: pickAttribute(attributes, ATTRIBUTE_ALIASES.subheading),
            heading: pickAttribute(attributes, ATTRIBUTE_ALIASES.heading),
            description: pickAttribute(attributes, ATTRIBUTE_ALIASES.description),
            buttonText: pickAttribute(attributes, ATTRIBUTE_ALIASES.buttonText),
            buttonLink: pickAttribute(attributes, ATTRIBUTE_ALIASES.buttonLink),
          };

          const used = Object.fromEntries(
            Object.entries(picks).filter(([, v]) => v).map(([k, v]) => [k, v.key])
          );
          const unknownKeys = Object.keys(attributes).filter(
            (k) => !Object.values(ATTRIBUTE_ALIASES).flat().includes(k)
          );

          console.log("[image-with-text-overlay] Personalisation result", {
            personalisationId,
            attributes,
            personalization,
            posture,
            usedAttributeKeys: used,
            ignoredAttributeKeys: unknownKeys,
          });

          if (picks.badge) this.applyBadge(picks.badge.value);
          if (picks.subheading) this.applySubheading(picks.subheading.value);
          if (picks.heading) this.applyHeading(picks.heading.value);
          if (picks.description) this.applyDescription(picks.description.value);
          if (picks.buttonText) this.applyButtonText(picks.buttonText.value);
          if (picks.buttonLink) this.applyButtonLink(picks.buttonLink.value);

          const imagePreloads = [];
          if (picks.desktopImage) {
            imagePreloads.push(
              preloadImage(picks.desktopImage.value).then((ok) => {
                if (ok) this.applyImage(picks.desktopImage.value, "desktop");
              })
            );
          }
          if (picks.mobileImage) {
            imagePreloads.push(
              preloadImage(picks.mobileImage.value).then((ok) => {
                if (ok) this.applyImage(picks.mobileImage.value, "mobile");
              })
            );
          }

          Promise.all(imagePreloads).then(() => {
            this.clearSkeleton();
            this.armViewTrace();
            this.armClickTrace();

            this.dispatchEvent(
              new CustomEvent("image-with-text-overlay:personalisation:ready", {
                bubbles: true,
                detail: {
                  personalisationId,
                  contentId,
                  decisionId: personalization.decisionId,
                  personalizationPointId: personalization.personalizationPointId,
                  personalizationPointName: personalization.personalizationPointName,
                  posture,
                  attributes,
                  personalization,
                  usedAttributeKeys: used,
                },
              })
            );
          });
        })
        .catch((err) => {
          clearTimeout(skeletonTimeout);
          console.warn("[image-with-text-overlay] Failed to fetch personalisation", personalisationId, err);
          const errorContext = {
            personalisationId,
            personalizationPointName: personalisationId,
            posture: "no_match",
            source: "fetch_error",
          };
          imageOverlayEmitTrace("decision", errorContext);
          imageOverlayEmitTrace("hidden_no_match", errorContext);
          this.clearSkeleton();
          this.dispatchEvent(
            new CustomEvent("image-with-text-overlay:personalisation:error", {
              bubbles: true,
              detail: { personalisationId, error: err },
            })
          );
        });
    }

    armViewTrace() {
      if (this._imageOverlayViewArmed) return;
      this._imageOverlayViewArmed = true;
      const fire = (source) => {
        const ctx = this._imageOverlayTraceContext;
        if (!ctx) return;
        imageOverlayEmitTrace("view", { ...ctx, source: source || "viewport" });
      };
      if (typeof IntersectionObserver === "function") {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              if (this._imageOverlayViewFired) return;
              this._imageOverlayViewFired = true;
              fire("viewport");
            });
          },
          { threshold: 0.5 }
        );
        observer.observe(this);
      } else {
        fire("fallback");
      }
    }

    armClickTrace() {
      if (this._imageOverlayClickArmed) return;
      this._imageOverlayClickArmed = true;
      this.addEventListener("click", (e) => {
        const ctx = this._imageOverlayTraceContext;
        if (!ctx) return;
        const clickEl = e.target && e.target.closest("a, button, [role='button']");
        if (!clickEl || !this.contains(clickEl)) return;
        if (clickEl.closest(".read-more-btn")) return;
        const href = clickEl.getAttribute("href") || undefined;
        imageOverlayEmitTrace("click", { ...ctx, source: "block_click", href });
      });
    }

    applyBadge(value) {
      const el = this.querySelector(".inline-badge");
      if (el) el.innerHTML = value;
    }

    applySubheading(value) {
      const el = this.querySelector(".subheading");
      if (el) el.innerHTML = value;
    }

    applyHeading(value) {
      const containsHtml = /<[a-z][\s\S]*>/i.test(value);
      const html = containsHtml ? value : value.replace(/\n/g, "<br>");
      this.querySelectorAll("h1, h2, h3, h4").forEach((el) => {
        el.innerHTML = html;
      });
    }

    applyDescription(value) {
      const fullText = this.querySelector(".rte .full-text");
      const mobileText = this.querySelector(".rte .mobile-text .text-content");
      if (fullText) fullText.innerHTML = value;
      if (mobileText) mobileText.innerHTML = value;

      if (!fullText && !mobileText) {
        const rte = this.querySelector(".rte");
        if (rte) rte.innerHTML = value;
      }
    }

    applyButtonText(value) {
      this.querySelectorAll(".button").forEach((btn) => {
        btn.textContent = value;
        btn.setAttribute("title", value);
      });
    }

    applyButtonLink(value) {
      this.querySelectorAll(".button").forEach((btn) => {
        if (btn.tagName === "A") btn.setAttribute("href", value);
      });
    }

    applyImage(url, variant) {
      const selector =
        variant === "mobile"
          ? "img.image-with-text-overlay--mobile, img[class*='--mobile']"
          : "img.image-with-text-overlay--desktop, img[class*='--desktop']";
      let img = this.querySelector(selector);
      if (!img && variant === "desktop") {
        img = this.querySelector("img");
      }
      if (!img) {
        const bg = this.querySelector(".image-with-text-overlay--bg");
        if (!bg) return;
        img = document.createElement("img");
        img.className =
          variant === "mobile"
            ? "responsive-image w-full image-with-text-overlay--mobile"
            : "responsive-image w-full image-with-text-overlay--desktop";
        img.setAttribute("alt", "");
        img.setAttribute("loading", "lazy");
        bg.appendChild(img);

        if (![...this.classList].some((c) => c.startsWith("mobile-height-"))) {
          this.classList.add("mobile-height-500");
        }
        [...this.classList]
          .filter((c) => c.startsWith("desktop-height-") || c.startsWith("mobile-height-"))
          .forEach((c) => bg.classList.add(c));

        this.classList.add("!pt-2.5", "md:!pt-[80px]", "!pb-0", "md:!pb-[80px]");

        this.style.removeProperty("background");
        this.style.removeProperty("opacity");
        this.style.setProperty("--text-color", "#ffffff");
        this.style.setProperty("--overlay-color-rgb", "0,0,0");
        this.style.setProperty("--overlay-opacity", "0.3");
        this.classList.add("max-[768px]:!bg-[#FAFAFA]", "max-[768px]:!opacity-100");
        this.querySelectorAll(".rte").forEach((rte) => {
          rte.classList.remove("[&>*]:text-[#787878]");
        });
      }

      img.setAttribute("src", url);
      img.removeAttribute("srcset");
      img.removeAttribute("data-src");
      img.removeAttribute("data-srcset");
      img.classList.add("lazyloaded");
      img.classList.remove("lazyload");
    }

    clearSkeleton() {
      if (!this.classList.contains("image-with-text-overlay--personalising")) return;
      this.classList.remove("image-with-text-overlay--personalising");
      const skeleton = this.querySelector(".image-with-text-overlay--skeleton");
      if (skeleton) setTimeout(() => skeleton.remove(), 400);
    }

    // prepareAnimations() {
    //   let section = this,
    //     button_offset = 0,
    //     property = (gsap.getProperty("html", "--header-height") + gsap.getProperty("html", "--header-offset")) + 'px';

    //   section.tl = gsap.timeline({
    //     scrollTrigger: {
    //       trigger: section,
    //       start: "top center"
    //     }
    //   });

    //   document.fonts.ready.then(function() {
    //     section.splittext = new SplitText(section.querySelectorAll('h3, p:not(.subheading)'), {
    //       type: 'lines, words',
    //       linesClass: 'line-child'
    //     });

    //     if (section.querySelector('.inline-badge')) {
    //       section.tl
    //         .fromTo(section.querySelector('.inline-badge'), {
    //           opacity: 0
    //         }, {
    //           duration: 0.75,
    //           opacity: 1
    //         }, 0);

    //       button_offset += 0.5;
    //     }
    //     if (section.querySelector('.subheading')) {
    //       section.tl
    //         .fromTo(section.querySelector('.subheading'), {
    //           opacity: 0
    //         }, {
    //           duration: 0.75,
    //           opacity: 0.6
    //         }, 0);

    //       button_offset += 0.5;
    //     }
    //     if (section.querySelector('h3')) {
    //       let h3_duration = 0.8 + ((section.querySelectorAll('h3 .line-child div').length - 1) * 0.08);
    //       section.tl
    //         .set(section.querySelector('h3'), {
    //           visibility: 'visible'
    //         }, 0)
    //         .from(section.querySelectorAll('h3 .line-child div'), {
    //           duration: h3_duration,
    //           yPercent: '100',
    //           stagger: 0.08
    //         }, 0);
    //       button_offset += h3_duration;
    //     }
    //     if (section.querySelector('.rte p')) {
    //       let p_duration = 0.8 + ((section.querySelectorAll('.rte p .line-child div').length - 1) * 0.02);
    //       section.tl
    //         .set(section.querySelectorAll('.rte p'), {
    //           visibility: 'visible'
    //         }, 0)
    //         .from(section.querySelectorAll('.rte p .line-child div'), {
    //           duration: p_duration,
    //           yPercent: '100',
    //           stagger: 0.02
    //         }, 0);
    //       button_offset += p_duration;
    //     }
    //     if (section.querySelectorAll('.button')) {
    //       let i = 1;
    //       section.querySelectorAll('.button').forEach((item) => {
    //         section.tl.fromTo(item, {
    //           autoAlpha: 0
    //         }, {
    //           duration: 0.5,
    //           autoAlpha: 1
    //         }, ((button_offset * 0.4) + (i - 1) * 0.1));
    //         i++;
    //       });
    //     }

    //   });

    //   if (section.querySelector('.thb-parallax-image')) {
    //     gsap.fromTo(section.querySelectorAll('.thb-parallax-image'), {
    //       y: '-8%'
    //     }, {
    //       y: '8%',
    //       ease: 'none',
    //       scrollTrigger: {
    //         trigger: section,
    //         scrub: 1,
    //         start: () => `top bottom`,
    //         end: () => `bottom top+=${property}`,
    //         onUpdate: () => {
    //           property = (gsap.getProperty("html", "--header-height") + gsap.getProperty("html", "--header-offset")) + 'px';
    //         }
    //       }
    //     });
    //   }
    // }
  }
  customElements.define("image-with-text-overlay", ImageTextOverlay);
}
